<script setup lang="ts">
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import ReportDraftsPanel from '@/components/createdUi/ReportDraftsPanel.vue'
/*
* Компонент UserProfile - профиль пользователя
*
* Основные функции:
* - Отображение персональных данных пользователя (read-only)
* - Отображение контактов (email, телефон) и статусов их подтверждения
* - Загрузка и удаление аватара пользователя
*
* Особенности:
* - Адаптивный дизайн для мобильных устройств
* - Интеграция с системой файлов (загрузка аватара через fileStore)
* - Редактирование данных профиля и настройки безопасности (пароль, 2FA, PIN)
*   делаются не здесь: PATCH /api/account/update/ удалён при переходе на Logto-only,
*   изменения профиля и безопасности теперь производятся на стороне Logto / бекенда
*/

// Импорты Vue и Vuetify
import axios from 'axios'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { VMaskInput } from 'vuetify/labs/VMaskInput'
import { BASE_URL } from '@/environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

// Импорты страниц для табов
import NotificationSettingsPage from '@/modules/notifications/pages/NotificationSettingsPage.vue'
import UserVisibilitySettings from './UserVisibilitySettings.vue'

// Импорты хранилищ
import { useFilesStore } from '@/modules/files/stores/filesStore'
import { useUserStore } from '@/modules/user/stores/userStore'

// Импорты утилит
import { formatDateOnly, formatDateTime } from '@/utils/formatters'

// Импорты компонентов
import DateMaskInput from '@/components/createdUi/DateMaskInput.vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import AvatarCropDialog from './AvatarCropDialog.vue'

// Импорты изображений
import avatar1 from '@images/avatars/avatar-1.png'
import profile_delete from '@images/icons/profile_delete.svg'
import profile_download from '@images/icons/profile_download.svg'

// Инициализация хранилищ
const userStore = useUserStore()
const fileStore = useFilesStore()

// Инициализация утилит отображения Vuetify
const { smAndDown, smAndUp, xs } = useDisplay()

// Логика управления табами
const route = useRoute()
const router = useRouter()

type TabType = 'profile' | 'settings' | 'visibility' | 'documents'
const documentsRefresh = ref(0)
const activeTab = ref<TabType>('profile')

const lastName = ref('')
const firstName = ref('')
const middleName = ref('')
const birthDate = ref<string | null>(null)
const email = ref('')
const phoneNumber = ref('')
const workPhoneNumber = ref('')

const resetForm = () => {
  if (userStore.user) {
    lastName.value = userStore.user.last_name || ''
    firstName.value = userStore.user.first_name || ''
    middleName.value = userStore.user.middle_name || ''
    birthDate.value = userStore.user.birth_date || null
    email.value = userStore.user.email || ''
    phoneNumber.value = userStore.user.phone_number || ''
    workPhoneNumber.value = userStore.user.work_phone_number || ''
  }
}

watch(() => userStore.user, () => {
  resetForm()
}, { immediate: true })

const hasChanges = computed(() => {
  if (!userStore.user) return false
  return (
    lastName.value !== (userStore.user.last_name || '')
    || firstName.value !== (userStore.user.first_name || '')
    || middleName.value !== (userStore.user.middle_name || '')
    || birthDate.value !== (userStore.user.birth_date || null)
    || email.value !== (userStore.user.email || '')
    || phoneNumber.value !== (userStore.user.phone_number || '')
    || workPhoneNumber.value !== (userStore.user.work_phone_number || '')
  )
})

const saveProfile = async () => {
  if (!hasChanges.value) return

  const payload = {
    last_name: lastName.value,
    first_name: firstName.value,
    middle_name: middleName.value,
    birth_date: birthDate.value,
    email: email.value,
    phone_number: phoneNumber.value,
    work_phone_number: workPhoneNumber.value,
  }

  await userStore.patchUserProfile(payload)
}

onMounted(async () => {
  const queryTab = route.query.tab as TabType
  if (queryTab && ['profile', 'settings', 'visibility', 'documents'].includes(queryTab)) {
    activeTab.value = queryTab
  }

  if (userStore.user) {
    await userStore.getConsentAgreement()
  }
})

watch(() => route.query.tab, value => {
  if (typeof value === 'string' && ['profile', 'settings', 'visibility', 'documents'].includes(value)) activeTab.value = value as TabType
})

watch(activeTab, newTab => {
  router.replace({ query: { ...route.query, tab: newTab } })
})

// Референсы компонентов
// Референс на скрытый input для загрузки файлов
const fileInput = ref<HTMLInputElement>()

// Реактивные состояния
// Выбранный файл для загрузки аватара
const selectedFile = ref<File | null>(null)

// Видимость уведомления о выборе файла
const showFileNotification = ref(false)

// Вычисляемые свойства данных пользователя
// Аватар пользователя (из профиля или дефолтный)
const userAvatar = computed(() => {
  return userStore.user?.avatar || avatar1
})

// Должность пользователя
const position = computed(() => userStore.user?.position?.name || '-')

// Отделение пользователя
const department = computed(() => userStore.user?.department?.name || '-')

// Флаг является ли пользователь руководителем
const isManager = computed(() => userStore.user?.is_manager)

const userRoles = computed(() => userStore.user?.roles ?? [])

// Ссылка на PDF-документ согласия с поддержкой абсолютных и относительных путей
const consentPdfUrl = computed(() => {
  const file = userStore.consentAgreement?.pdf_file
  if (!file) return ''
  if (file.startsWith('http://') || file.startsWith('https://')) {
    return file
  }
  const prefix = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL
  const filePath = file.startsWith('/') ? file : `/${file}`
  return `${prefix}${filePath}`
})

/**
 * Открытие диалога выбора файла для загрузки аватара
 *
 * Назначение:
 * - Программно активирует скрытый input типа file
 * - Позволяет пользователю выбрать файл для загрузки аватара
 *
 * Действия:
 * - Вызывает click() на скрытом input элементе
 * - Открывает системный диалог выбора файла
 */
const triggerFileInput = () => {
  fileInput.value?.click()
}

// Состояния диалога кадрирования аватара
const isCropDialogOpen = ref(false)
const cropImageSrc = ref('')
const cropFileName = ref('avatar.jpg')
let cropObjectUrl: string | null = null

const cleanupCrop = () => {
  if (cropObjectUrl) {
    URL.revokeObjectURL(cropObjectUrl)
    cropObjectUrl = null
  }
  cropImageSrc.value = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

onBeforeUnmount(() => {
  cleanupCrop()
})

/**
 * Обработчик выбора файла для аватара - открывает кадрирование
 */
const handleFileUpload = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0] || null

  if (file) {
    cleanupCrop()
    selectedFile.value = file
    cropFileName.value = file.name
    cropObjectUrl = URL.createObjectURL(file)
    cropImageSrc.value = cropObjectUrl
    isCropDialogOpen.value = true
  }
}

/**
 * Обработчик применения кадрированного изображения
 */
const onCropConfirm = async (file: File) => {
  cleanupCrop()
  selectedFile.value = file
  showFileNotification.value = true
  await userStore.setUserAvatar(file)
}

/**
 * Отмена кадрирования
 */
const onCropCancel = () => {
  cleanupCrop()
}

/**
 * Функция удаления аватара пользователя
 *
 * Назначение:
 * - Удаляет текущий аватар пользователя с сервера
 * - Восстанавливает аватар по умолчанию
 * - Обновляет данные профиля после удаления
 *
 * Действия:
 * 1. Вызывает deleteUserAvatar из userStore
 * 2. Обновляет данные профиля для отображения изменений
 * 3. Восстанавливает дефолтный аватар в интерфейсе
 */
const handleDeleteAvatar = async () => {
  await userStore.deleteUserAvatar()
}
</script>

<template>
  <div class="d-flex flex-column gap-4">
    <VTabs
      v-model="activeTab"
      class="user-profile__tabs mb-5"
      color="primary"
    >
      <VTab value="profile">
        <VIcon start icon="ri-user-3-line" />
        Профиль
      </VTab>
      <VTab value="settings">
        <VIcon start icon="ri-settings-3-line" />
        Настройки уведомлений
      </VTab>
      <VTab value="visibility">
        <VIcon start icon="ri-eye-line" />
        Настройки видимости
      </VTab>
      <VTab value="documents"><VIcon start icon="ri-draft-line" />Мои заявления</VTab>
    </VTabs>

    <VWindow v-model="activeTab" :touch="false">
      <VWindowItem value="profile">
        <VCard class="user-profile">
          <VRow
            align="center"
            :class="{ 'px-3': smAndDown }"
            class="auth-wrapper min-h-100dvh w-full"
            no-gutters
          >
            <!-- Header -->
            <VCol cols="12" class="user-profile__header">
              <header class="d-flex w-full justify-space-between" style="max-width: 68rem;">
                <div class="header__avatar d-flex align-center gap-3">
                  <VAvatar
                    color="primary"
                    variant="tonal"
                    size="100"
                    rounded="circle"
                  >
                    <VImg
                      :src="userAvatar"
                      contain
                    />
                  </VAvatar>

                  <div class="d-flex flex-column">
                    <VBtn
                      variant="plain"
                      :ripple="false"
                      class="cursor-pointer pa-0 ml-2"
                      :append-icon="profile_download"
                      color="secondary"
                      @click="triggerFileInput"
                    >
                      Загрузить
                      <input
                        ref="fileInput"
                        type="file"
                        accept="image/*"
                        class="d-none"
                        @change="handleFileUpload"
                      >
                    </VBtn>

                    <!-- Кнопка удаления -->
                    <VBtn
                      v-if="userStore.user?.avatar"
                      variant="plain"
                      :ripple="false"
                      class="cursor-pointer pa-0"
                      :append-icon="profile_delete"
                      color="secondary"
                      @click="handleDeleteAvatar"
                    >
                      Удалить
                    </VBtn>

                    <!-- Уведомление о выбранном файле -->
                    <VSnackbar
                      v-model="showFileNotification"
                      timeout="7000"
                      color="primary"
                    >
                      Выбран файл: {{ selectedFile?.name }}
                    </VSnackbar>
                  </div>
                </div>
                <div v-if="isManager && !smAndDown">
                  <VChip color="primary" class="d-flex flex-row justify-center align-center px-3 py-0.5 gap-1" size="small">
                    Руководитель
                  </VChip>
                </div>
              </header>
            </VCol>

            <!-- User Info -->
            <VCol cols="12 mt-5">
              <div class="user-profile__info d-flex flex-column gap-5" style="max-width: 68rem;">
                <!-- First line -->
                <div class="d-flex flex-wrap gap-3" :class="{ 'flex-column': smAndDown }">
                  <VTextField
                    v-model="lastName"
                    variant="outlined"
                    label="Фамилия"
                    persistent-placeholder
                  />
                  <VTextField
                    v-model="firstName"
                    variant="outlined"
                    label="Имя"
                    persistent-placeholder
                  />
                  <VTextField
                    v-model="middleName"
                    variant="outlined"
                    label="Отчество"
                    persistent-placeholder
                  />
                </div>

                <!-- Second line -->
                <div class="d-flex flex-wrap gap-3 align-center" :class="{ 'flex-column': smAndDown }">
                  <VTextField
                    :model-value="position"
                    disabled
                    variant="outlined"
                    label="Должность"
                    persistent-placeholder
                    :style="smAndDown ? { width: '100%' } : { width: '57.5%' }"
                  />

                  <DateMaskInput
                    v-model="birthDate"
                    class="flex-grow-1"
                    density="comfortable"
                    :style="smAndDown ? { width: '100%' } : undefined"
                  >
                    <template #label>
                      <span>Дата рождения</span>
                    </template>
                  </DateMaskInput>
                </div>

                <!-- Third line -->
                <div class="d-flex flex-column w-68">
                  <VTextField
                    :model-value="department"
                    disabled
                    variant="outlined"
                    label="Отделение"
                    persistent-placeholder
                  />
                </div>

                <!-- Contacts Divider -->
                <CustomDivider title="Контакты" class="mt-4" />

                <!-- Contacts Inputs Column -->
                <div class="d-flex flex-column gap-4 w-68">
                  <VTextField
                    v-model="email"
                    label="Электронная почта"
                    variant="outlined"
                    persistent-placeholder
                    :class="smAndDown ? 'w-100' : 'w-22'"
                  />

                  <VTextField
                    v-model="workPhoneNumber"
                    label="Рабочий телефон"
                    variant="outlined"
                    persistent-placeholder
                    :class="smAndDown ? 'w-100' : 'w-22'"
                  />

                  <VMaskInput
                    v-model="phoneNumber"
                    mask="+7 (###) ###-##-##"
                    variant="outlined"
                    persistent-placeholder
                    hide-details
                    density="comfortable"
                    :class="smAndDown ? 'w-100' : 'w-22'"
                  >
                    <template #label>
                      <span>Мобильный телефон</span>
                    </template>
                  </VMaskInput>
                </div>

                <template v-if="userRoles.length">
                  <CustomDivider title="Роли" class="mt-4" />

                  <div
                    class="user-profile__roles w-68"
                    :class="{ 'user-profile__roles--stacked': smAndDown }"
                  >
                    <template
                      v-for="(role, index) in userRoles"
                      :key="role.name"
                    >
                      <div
                        class="d-flex align-center"
                        :class="smAndDown ? 'pt-3 pb-1' : 'py-3'"
                      >
                        <VChip
                          size="small"
                          variant="tonal"
                          color="secondary"
                          class="user-profile__roles-chip"
                        >
                          {{ role.name }}
                        </VChip>
                      </div>
                      <div
                        class="d-flex align-center"
                        :class="smAndDown ? 'pt-1 pb-3' : 'py-3'"
                      >
                        <span class="text-body-1">{{ role.description }}</span>
                      </div>
                      <VDivider
                        v-if="index < userRoles.length - 1"
                        class="user-profile__roles-divider"
                      />
                    </template>
                  </div>
                </template>

                <!-- Save Button -->
                <div class="d-flex justify-start mt-4">
                  <VBtn
                    color="primary"
                    :disabled="!hasChanges"
                    :block="smAndDown"
                    @click="saveProfile"
                  >
                    Сохранить
                  </VBtn>
                </div>

                <!-- Ссылка на Согласие на обработку ПД -->
                <div class="d-flex align-center mt-4">
                  <VIcon start icon="ri-file-pdf-line" :color="consentPdfUrl ? 'primary' : 'secondary'" class="me-2" />
                  <a
                    v-if="consentPdfUrl"
                    :href="consentPdfUrl"
                    target="_blank"
                    class="consent-pdf-link font-weight-medium text-decoration-none"
                  >
                    Согласие на обработку персональных данных (подписано {{ formatDateTime(userStore.consentAgreement?.created_at) }})
                  </a>
                  <span v-else class="text-medium-emphasis text-body-1 font-weight-medium">
                    Согласие на обработку персональных данных (документ не найден)
                  </span>
                </div>
              </div>
            </VCol>
          </VRow>
        </VCard>
      </VWindowItem>

      <VWindowItem value="settings">
        <NotificationSettingsPage />
      </VWindowItem>

      <VWindowItem value="visibility">
        <UserVisibilitySettings />
      </VWindowItem>
      <VWindowItem value="documents">
        <VCard class="pa-4 pa-sm-6">
          <div class="d-flex align-center justify-space-between gap-3 mb-4">
            <div><h2 class="text-h5">Мои заявления и документы</h2><p class="text-body-2 mt-2">Выберите форму справа. Ваши ФИО и должность подставятся из профиля.</p></div>
            <ReportTemplateMenu v-if="userStore.user?.id" object-type="employee" :object-id="userStore.user.id" variant="toolbar" @saved="documentsRefresh++" />
          </div>
          <ReportDraftsPanel v-if="activeTab === 'documents'" :refresh-key="documentsRefresh" />
        </VCard>
      </VWindowItem>
    </VWindow>

    <!-- Диалог кадрирования аватара -->
    <AvatarCropDialog
      v-model="isCropDialogOpen"
      :image-src="cropImageSrc"
      :file-name="cropFileName"
      @confirm="onCropConfirm"
      @cancel="onCropCancel"
    />
  </div>
</template>

<style lang="scss">
@use "@core/scss/base/placeholders/custom-styles" as *;

.user-profile {
  @extend %component-default;
}

.user-profile__tabs {
  width: max-content;
  max-width: 100%;
}

.w-22 {
  max-width: 22rem;
  width: 100%;
}

.adjacent-label {
  height: 0.9375rem;
  font-weight: 600;
  font-size: 0.8125rem;
  line-height: 0.9375rem;
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
  flex: none;
  order: 0;
  flex-grow: 0;
  margin-bottom: 0.25rem;
}

.w-68 {
  max-width: 68rem;
}

.user-profile__roles {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: 90px;
  align-items: center;
}

.user-profile__roles--stacked {
  grid-template-columns: minmax(0, 1fr);
  column-gap: 0;
}

.user-profile__roles-chip {
  max-width: 100%;
  height: auto;
  white-space: normal;
}

.user-profile__roles-divider {
  grid-column: 1 / -1;
}

.confirm {
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  flex: none;
  cursor: pointer;

  &.confirmed {
    color: #56CA00;
    cursor: default;
  }

  &.unconfirmed {
    color: rgb(var(--v-theme-secondary)) !important;
    &:hover {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }
}

.consent-pdf-link {
  color: rgb(var(--v-theme-primary)) !important;
  font-size: 0.9375rem;
  &:hover {
    text-decoration: underline !important;
    color: rgba(var(--v-theme-primary), 0.8) !important;
  }
}
</style>
